<template>
  <div
    class="custom-animation tw-relative tw-h-full tw-rounded tw-border tw-border-1 tw-border-solid tw-border-[#EFF0F1] tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)]"
  >
    <div
      id="dash-categories-card-title"
      class="tw-h-[66px] tw-rounded-t tw-flex tw-justify-between tw-items-center tw-bg-[#FCFCFD] tw-py-[25px] tw-px-[20px] tw-border-b-[1px] tw-border-0 tw-border-solid tw-border-[#EFF0F1]"
    >
      <div class="tw-flex tw-gap-[10px]">
        <span class="tw-text-[#211F58] tw-text-[18px] tw-font-semibold">
          {{ getComponentUiText("app-dashboard-category-title-1") }}
        </span>
      </div>
    </div>
    <div class="tw-p-4">
      <RadarChart
        :id="'dash-categories-chart'"
        :height="750"
        :series="[
          // {
          //   name: getComponentUiText(
          //     'app-dashboar-categories-chart-potential-1',
          //   ),
          //   data: dataTest2,
          //   color: '#57B755',
          // },
          {
            name: getComponentUiText('app-dashboar-categories-chart-current-1'),
            data: categoriesData,
            color: '#904BD6',
          },
        ]"
        :chart-options="chartOptions"
        :loading="isLoading"
        :height-loading="750"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType } from "vue";
import { getComponentUiText } from "@/utils/utilities";
import RadarChart from "../chart/radarChart.vue";
import { ICategories } from "@/interfaces/assessments/ICategories";

const props = defineProps({
  data: {
    type: Object as PropType<ICategories[]>,
    default: () => {
      return [];
    },
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
    required: false,
  },
});
const isLoading = computed(() => {
  return props.loading;
});

const categoriesName = computed(() => {
  return props.data.map((item) => item.checklistItemCategoryName);
});
const categoriesData = computed(() => {
  if (!props.data || props.data.length === 0) {
    return [];
  }
  return props.data.map((item) =>
    (item.checklistCategoryScorePcnt * 10).toFixed(1),
  );
});

const xaxisLabelColors = computed(() => {
  const lengCate = categoriesName.value.length;
  const colors: string[] = [];
  for (let i = 0; i < lengCate; i++) {
    colors.push("#387DFF");
  }
  return colors;
});
const chartOptions = computed(() => {
  return {
    chart: { toolbar: { show: false } },
    plotOptions: {
      radar: {
        polygons: {
          strokeColor: "#e8e8e8",
          strokeWidth: 1,
          connectorColors: "#e8e8e8",
          fill: {
            colors: ["#F9F9FB", "#fff"],
          },
        },
        dataLabels: {
          name: {
            show: true,
          },
          value: {
            show: true,
            color: "#FCFCFD",
            fontSize: "14px",
            fontFamily: "Inter",
            fontWeight: 400,
            offsetY: 5,
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
      background: {
        enabled: true,
        foreColor: "#fff", // Text color
        borderRadius: 2,
        padding: 4,
        opacity: 0.9,
        borderWidth: 1,
        borderColor: "#fff",
      },
    },
    xaxis: {
      categories: categoriesName.value,
      labels: {
        show: true,
        trim: true,
        hideOverlappingLabels: true,
        style: {
          colors: xaxisLabelColors.value,
          fontSize: "9px",
          fontWeight: 500,
          fontFamily: "Poppins",
          lineHeight: "130%",
          cssClass: "custom-xaxis-label",
        },
      },
    },
    yaxis: {
      show: true,
      stepSize: 1,
      // min: 0,
      // max: 10,
      labels: {
        show: true,
        style: {
          colors: [
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
            "#B7B7B7",
          ],
          fontSize: "11px",
          fontWeight: 500,
          fontFamily: "Poppins",
          lineHeight: "130%",
        },
        offsetY: 20,
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "diagonal1",
        // shadeIntensity: 0.5,
        // gradientToColors: ['rgba(18, 129, 0, 0.20)', 'rgba(225, 160, 255, 0.61)' ],
        inverseColors: true,
        opacityFrom: 0.9,
        opacityTo: 0.6,
        stops: [0, 5, 10],
        colorStops: [],
      },
      
    },
    stroke: {
      show: true,
      width: 1,
      // colors: ["#57B755", "#904BD6"],
      colors: ["#904BD6"],
      opacity: 1,
    },
    markers: {
      // strokeColors: ["#57B755", "#904BD6"],
      strokeColors: ["#904BD6"],
      colors: ["#FFFFFF"],
      shape: "circle",
      size: 4,
      hover: {
        size: 6,
        sizeOffset: 3,
      },
    },
    tooltip: {
      enabled: true,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        // const bgColor = seriesIndex === 0 ? "#57B755" : "#904BD6";
        const bgColor = seriesIndex === 0 ? "#904BD6" : "#57B755";
        return (
          `<div class="categories-chart-tooltip" style="background-color: ${bgColor}">` +
          "<span >" +
          w.globals.labels[dataPointIndex] +
          " - " +
          series[seriesIndex][dataPointIndex] +
          "</span>" +
          "</div>"
        );
      },
    },
    legend: {
      horizontalAlign: "left",
      showForSingleSeries: true,
      showForNullSeries: true,
      showForZeroSeries: true,
      fontSize: "14px",
      fontFamily: "Poppins",
      fontWeight: 500,
      labels: {
        colors: ["#878787", "#878787"],
      },
      markers: {
        size: 9,
        // fillColors: ["#57B755", "#904BD6"],
        fillColors: ["#904BD6"],
      },
    },
  };
});

onMounted(async () => {});

const dataTest2 = [
  2.3, 6.9, 5.3, 4.5, 3.2, 2.1, 9.9, 10, 1.0, 0.5, 6.5, 7.5, 8.5, 9.5, 10.0,
  9.0, 8.0, 7.0, 6.0, 5.0, 4.0, 3.0, 2.0, 1.0, 2.5, 3.5, 4.5, 5.5, 5.5, 6.6,
  7.7, 8.8, 2.8,
];
</script>

<style scoped>
.default {
  background: var(--ui-white, #fff);
  color: var(--ui-dark-grey, #878787);
  border-radius: 4px;
  border: 1px solid var(--ui-rule, #eff0f1);
}

@media screen and (min-width: 1920px) {
  .vendor-ass-card-body {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
